import React, { useCallback, useEffect, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import KeyboardArrowDownRounded from '@mui/icons-material/KeyboardArrowDownRounded';
import LocationOnRounded from '@mui/icons-material/LocationOnRounded';
import MyLocationRounded from '@mui/icons-material/MyLocationRounded';
import { useNavigate } from 'react-router-dom';

import Button from '../components/Button';
import { useV2Session } from '../state/SessionProvider';
import { findNearestStoreByCoords } from '../services/store';
import {
  checkPermission,
  getCurrentPosition,
  requestLocationPermission,
} from '../platform/geolocation';
import { storageGet, storageSet } from '../platform/storage';

const CACHE_KEY = 'v2_location';
const CACHE_TTL_MS = 30 * 60 * 1000; // 30 minutes

// Cache shape: { areaName, storeName, storeId, lat, lng, cachedAt }
async function readCache() {
  const c = await storageGet(CACHE_KEY);
  if (!c || Date.now() - c.cachedAt > CACHE_TTL_MS) return null;
  return c;
}

async function writeCache(loc) {
  await storageSet(CACHE_KEY, { ...loc, cachedAt: Date.now() });
}

async function reverseGeocode({ lat, lng }) {
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`,
      { headers: { 'Accept-Language': 'en' } }
    );
    const json = await res.json();
    const a = json?.address ?? {};
    return (
      a.suburb || a.neighbourhood || a.village ||
      a.town || a.city_district || a.city || a.county || null
    );
  } catch {
    return null;
  }
}

export default function Location({ savedAddressesCount = 0 }) {
  const navigate = useNavigate();
  const { loggedIn } = useV2Session();

  const [perm, setPerm] = useState(null);
  const [busy, setBusy] = useState(true);
  const [location, setLocation] = useState(null); // { areaName, storeName, lat, lng }
  const [locError, setLocError] = useState(null);

  const resolve = useCallback(async (pos) => {
    if (!pos) { setLocError('Could not read your location'); setLocation(null); return; }
    try {
      const [storeData, areaName] = await Promise.all([
        findNearestStoreByCoords(pos),
        reverseGeocode(pos),
      ]);
      const loc = {
        areaName,
        storeName: storeData?.store?.storeName ?? null,
        storeId: storeData?.store?.id ?? null,
        lat: pos.lat,
        lng: pos.lng,
      };
      setLocation(loc);
      setLocError(null);
      await writeCache(loc);
    } catch (e) {
      setLocation(null);
      setLocError(e.message || 'No store found for this area');
    }
  }, []);

  const runGrantedFlow = useCallback(async () => {
    setBusy(true);
    setLocError(null);
    const pos = await getCurrentPosition();
    await resolve(pos);
    setBusy(false);
  }, [resolve]);

  useEffect(() => {
    let cancelled = false;

    async function init() {
      setBusy(true);

      // Show cached data immediately — no spinner
      const cached = await readCache();
      if (cached && !cancelled) {
        setLocation({ areaName: cached.areaName, storeName: cached.storeName, lat: cached.lat, lng: cached.lng });
        setBusy(false);
      }

      let p = await checkPermission();
      if (cancelled) return;

      if (p === 'prompt') {
        p = await requestLocationPermission();
        if (cancelled) return;
      }

      setPerm(p);

      if (p === 'granted') {
        if (cached) return; // fresh cache — skip API call
        if (!cancelled) setBusy(true);
        const pos = await getCurrentPosition();
        if (cancelled) return;
        await resolve(pos);
      }

      if (!cancelled) setBusy(false);
    }

    init();
    return () => { cancelled = true; };
  }, [resolve]);

  const onUseMyLocation = async (e) => {
    e?.stopPropagation();
    setBusy(true);
    setLocError(null);
    const p = await requestLocationPermission();
    setPerm(p);
    if (p !== 'granted') { setBusy(false); return; }
    await runGrantedFlow();
  };

  const hasLocation = !busy && perm === 'granted' && (location?.areaName || location?.storeName);
  const needsPermission = !busy && (perm === 'prompt' || perm === 'denied');

  return (
    <Box
      onClick={() => navigate('/v2/location')}
      role="button"
      aria-label="Change delivery location"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 2,
        py: 1.25,
        mb: 2,
        bgcolor: '#fff8f0',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        cursor: 'pointer',
        userSelect: 'none',
        '&:active': { bgcolor: '#fff0e0' },
      }}
    >
      <LocationOnRounded sx={{ color: 'primary.main', fontSize: 28, flexShrink: 0, mt: '2px' }} />

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="caption"
          sx={{ color: 'text.secondary', fontWeight: 500, lineHeight: 1, display: 'block', mb: 0.25 }}
        >
          Delivering to
        </Typography>

        {busy && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <CircularProgress size={12} thickness={5} />
            <Typography variant="body2" color="text.secondary">Locating…</Typography>
          </Box>
        )}

        {hasLocation && (
          <>
            <Typography variant="subtitle1" fontWeight={700} noWrap sx={{ lineHeight: 1.25 }}>
              {location.areaName ?? location.storeName}
            </Typography>
            {location.storeName && location.areaName && (
              <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', lineHeight: 1.3 }}>
                {location.storeName}
              </Typography>
            )}
          </>
        )}

        {needsPermission && (
          <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.25, color: 'text.secondary' }}>
            Set your location
          </Typography>
        )}

        {!busy && perm === 'granted' && !location?.storeName && locError && (
          <Typography variant="caption" color="error" display="block">{locError}</Typography>
        )}
      </Box>

      {needsPermission && perm === 'prompt' ? (
        <Button
          size="small"
          startIcon={<MyLocationRounded sx={{ fontSize: 14 }} />}
          sx={{ flexShrink: 0, fontSize: 12, fontWeight: 600, py: 0.5, px: 1.25, borderRadius: 5 }}
          onClick={onUseMyLocation}
        >
          Detect
        </Button>
      ) : (
        <KeyboardArrowDownRounded sx={{ color: 'text.secondary', fontSize: 22, flexShrink: 0 }} />
      )}
    </Box>
  );
}
